import type { OriginAnalyticsAttributes } from '@atlassiansox/origin-tracing/types';
import type { OrgSettings } from '@atlassian/integrations/third-party';

import { BaseInvitePeopleAnalytics, type FailedFetchAttributes } from '../../analytics';
import type { PluginLocation } from '../../plugins/types';
import {
	AnalyticsActionSubject,
	AnalyticsAction,
	type InviteResponseStatus,
	type ExternalUserType,
	type FeaturedUser,
} from '../../types';
import { type UserSource } from '@atlaskit/user-picker/types';
import type { TrackEventAttributes } from '@atlassian/expansion-services/services';
import { fg } from '@atlaskit/platform-feature-flags';
import { AnyAri } from '@atlassian/ari/any-ari';

interface InviteResponseDetails {
	invited?: string[];
	accessRequested?: string[];
	error?: {
		message: string;
		code?: string;
	}[];
}

interface UserIdAttributes {
	originAttributes: OriginAnalyticsAttributes | undefined;
	userId: string;
	products: string[];
	inviteResult: {
		[resourceAri: string]: InviteResponseStatus;
	};
	externalUserType?: ExternalUserType;
	externalUserSubtype?: 'slackChannel';
	isAioliv3Enabled?: boolean;
	isUserEmailVisible: boolean;
	sources?: UserSource[];
	inviteFromSuccessModal?: boolean;
	hasPreselectedTwc: boolean;
}

export const PRODUCT_SELECT_SUBJECT_ID = 'productSelect';

export class InvitePeopleAnalytics extends BaseInvitePeopleAnalytics {
	inviteRequestSucceeded(
		products: string[],
		response: InviteResponseDetails,
		isRecaptchaTokenPresent: boolean,
		inviteFromSuccessModal?: boolean,
	): void {
		const { source, userRole } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.INVITE_REQUEST,
			action: AnalyticsAction.SUCCEEDED,
			attributes: {
				integration: source,
				permissionLevel: userRole,
				products,
				invited: response.invited,
				accessRequested: response.accessRequested,
				error: response.error,
				isRecaptchaTokenPresent,
				inviteFromSuccessModal,
			},
		});
	}

	productSelectDropdownOpened(attributes: {
		selectedProducts?: string[];
		productOptions?: string[];
	}): void {
		const { source, userRole } = this.commonAttributes;
		const { productOptions, selectedProducts } = attributes;

		this.fireUI({
			actionSubject: AnalyticsActionSubject.DROPDOWN,
			actionSubjectId: PRODUCT_SELECT_SUBJECT_ID,
			action: AnalyticsAction.OPENED,
			attributes: {
				integration: source,
				source,
				userRole,
				productOptions,
				selectedProducts,
			},
		});
	}

	userInvited(attributes: UserIdAttributes): void {
		const {
			originAttributes,
			userId,
			inviteResult,
			externalUserType,
			externalUserSubtype,
			isUserEmailVisible,
			...rest
		} = attributes;
		const {
			inviteFromAssignModalCohort,
			isEligibleForInviteAndAssignFromBoard,
			source,
			userRole,
			experimentalFeatures,
			productId,
			accountId,
			cloudId,
			decorateAnalyticsAttributes,
		} = this.commonAttributes;

		Object.entries(inviteResult).forEach(([resourceAri, status]) => {
			const ari = AnyAri.parse(resourceAri);
			const trackAttributes: TrackEventAttributes = {
				actorAaid: accountId!,
				sourceProduct: productId as TrackEventAttributes['sourceProduct'],
				sourceTenantId: cloudId!,
				userAaid: userId,
				targetProduct: ari.resourceOwner as TrackEventAttributes['targetProduct'],
				targetTenantId: ari.resourceId,
				inviteStatus: status,
				integration: source,
				touchpoint: 'Core Invites',
			};

			const attributes = {
				...originAttributes,
				permissionLevel: userRole,
				inviteResult: [{ resourceAri, status }],
				externalUserType,
				externalUserSubtype,
				isUserEmailVisible,
				...(isEligibleForInviteAndAssignFromBoard && {
					inviteAndAssignFromBoardCohort: 'control',
				}),
				experimentalFeatures,
				inviteFromAssignModalCohort,
				...rest,
				products: [ari.resourceOwner],
				...trackAttributes,
			};

			// eslint-disable-next-line @atlaskit/platform/no-preconditioning
			if (fg('jira-1073-additional-attrs') && decorateAnalyticsAttributes) {
				this.fireTrack({
					actionSubject: AnalyticsActionSubject.USER,
					actionSubjectId: userId,
					action: AnalyticsAction.INVITED,
					attributes: decorateAnalyticsAttributes(attributes),
				});
			} else {
				this.fireTrack({
					actionSubject: AnalyticsActionSubject.USER,
					actionSubjectId: userId,
					action: AnalyticsAction.INVITED,
					attributes,
				});
			}
		});
	}

	userAccessRequested(attributes: UserIdAttributes): void {
		const {
			originAttributes,
			userId,
			inviteResult,
			externalUserType,
			externalUserSubtype,
			isUserEmailVisible,
			...rest
		} = attributes;
		const { inviteFromAssignModalCohort, source, userRole, accountId, productId, cloudId } =
			this.commonAttributes;

		Object.entries(inviteResult).forEach(([resourceAri, status]) => {
			const ari = AnyAri.parse(resourceAri);
			const trackAttributes: TrackEventAttributes = {
				actorAaid: accountId!,
				sourceProduct: productId as TrackEventAttributes['sourceProduct'],
				sourceTenantId: cloudId!,
				userAaid: userId,
				targetProduct: ari.resourceOwner as TrackEventAttributes['targetProduct'],
				targetTenantId: ari.resourceId,
				inviteStatus: status,
				integration: source,
				touchpoint: 'Core Invites',
			};

			this.fireTrack({
				actionSubject: AnalyticsActionSubject.PRODUCT_ACCESS,
				actionSubjectId: ari.resourceOwner,
				action: AnalyticsAction.REQUESTED,
				attributes: {
					...originAttributes,
					permissionLevel: userRole,
					requestedForUserId: userId,
					inviteResult: [{ resourceAri, status }],
					externalUserType,
					externalUserSubtype,
					isUserEmailVisible,
					inviteFromAssignModalCohort,
					...rest,
					products: [ari.resourceOwner],
					...trackAttributes,
				},
			});
		});
	}

	inviteRequestFailed(
		products: string[],
		isRecaptchaTokenPresent: boolean,
		code?: string,
		message?: string,
		status?: number,
		inviteFromSuccessModal?: boolean,
	): void {
		const { source, userRole } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.INVITE_REQUEST,
			action: AnalyticsAction.FAILED,
			attributes: {
				code,
				message,
				permissionLevel: userRole,
				products,
				integration: source,
				status,
				isRecaptchaTokenPresent,
				inviteFromSuccessModal,
			},
		});
	}

	fetchingEligibleUsersFailed(products: string[]): void {
		const { source, userRole } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.FETCH_ELIGIBLE_USERS,
			action: AnalyticsAction.FAILED,
			attributes: {
				permissionLevel: userRole,
				products,
				integration: source,
			},
		});
	}

	userInviteSettingsFailed(
		attributes: {
			proposedNewState: string;
			productAri: string;
		} & FailedFetchAttributes,
	): void {
		const { flow } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.USER_INVITE_SETTINGS,
			action: AnalyticsAction.UPDATE_FAILED,
			attributes: {
				flow,
				...attributes,
			},
		});
	}

	inviteFormLoaded(duration: number, hasPermissionToInviteOrRequestAccess: boolean): void {
		const { source } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.INVITE_FORM,
			action: AnalyticsAction.LOADED,
			attributes: {
				integration: source,
				duration,
				hasPermissionToInviteOrRequestAccess,
			},
		});
	}

	componentCompleteLoaded(duration: number, hasPermissionToInviteOrRequestAccess: boolean): void {
		const { source } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.CORE_INVITE_COMPONENT,
			action: AnalyticsAction.LOADED,
			attributes: {
				integration: source,
				duration,
				hasPermissionToInviteOrRequestAccess,
			},
		});
	}

	inviteComponentViewed(
		attributes: {
			hasGmailDomain?: boolean;
			isConnectedToSlack: boolean;
		} & OrgSettings,
	): void {
		const {
			disableNoInviteError,
			inviteFromAssignModalCohort,
			plugins,
			productTitle,
			source,
			userRole,
			subProduct,
			viewAs,
			experimentalFeatures,
			applicationEdition,
		} = this.commonAttributes;
		const pluginsLocation: PluginLocation[] | undefined = plugins
			? Object.keys(plugins).map((location) => location as PluginLocation)
			: undefined;
		this.fireScreen({
			name: AnalyticsActionSubject.CORE_INVITE_COMPONENT,
			attributes: {
				...attributes,
				...(fg('core_invites_add_project_and_edition_to_events')
					? { subProduct, applicationEdition }
					: {}),
				disableNoInviteError,
				inviteFromAssignModalCohort,
				pluginsLocation,
				product: productTitle,
				source,
				userRole,
				viewAs,
				invitePromptOnLinkV3Cohort: experimentalFeatures?.invitePromptOnLinkV3 ?? false,
			},
		});
	}

	getPermsFailed(resourceId: string, message: string): void {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.PERMS,
			actionSubjectId: resourceId,
			action: AnalyticsAction.FAILED,
			attributes: {
				message,
			},
		});
	}

	getPermsSucceeded(
		resourceId: string,
		isInviteUsersPermitted?: boolean,
		isManagePermitted?: boolean,
	): void {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.PERMS,
			actionSubjectId: resourceId,
			action: AnalyticsAction.SUCCEEDED,
			attributes: {
				isInviteUsersPermitted,
				isManagePermitted,
			},
		});
	}

	accountInfoFailed(error: string): void {
		this.fireOperational({
			actionSubject: AnalyticsActionSubject.ACCOUNT_INFO,
			action: AnalyticsAction.FAILED,
			attributes: {
				error,
			},
		});
	}

	approvedDomainsSettingsFailed(
		attributes: {
			proposedDomains: string[];
		} & FailedFetchAttributes,
	): void {
		const { flow } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.APPROVED_DOMAIN_SETTINGS,
			action: AnalyticsAction.UPDATE_FAILED,
			attributes: {
				flow,
				...attributes,
			},
		});
	}

	inviteFailedSLO(isAioliv3Enabled: boolean, code?: string, message?: string, status?: number): void {
		const { source } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.SLO_INVITE_REQUEST,
			action: AnalyticsAction.FAILED,
			attributes: {
				isAioliv3Enabled,
				status,
				code,
				message,
				integration: source,
			},
		});
	}

	inviteSucceededSLO(isAioliv3Enabled: boolean): void {
		const { source } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.SLO_INVITE_REQUEST,
			action: AnalyticsAction.SUCCEEDED,
			attributes: {
				isAioliv3Enabled,
				integration: source,
			},
		});
	}

	approvedDomainsSettingsSucceededSLO(attributes: { addedDomains: string[] }): void {
		const { flow } = this.commonAttributes;

		this.fireTrack({
			// Matches naming from admin hub
			actionSubject: AnalyticsActionSubject.APPROVED_DOMAIN_SETTINGS,
			action: AnalyticsAction.UPDATED,
			attributes: {
				flow,
				...attributes,
			},
		});
	}

	approvedDomainsSettingsFailedSLO(
		attributes: {
			proposedDomains: string[];
		} & FailedFetchAttributes,
	): void {
		const { flow } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.SLO_APPROVED_DOMAIN_SETTINGS,
			action: AnalyticsAction.UPDATE_FAILED,
			attributes: {
				flow,
				...attributes,
			},
		});
	}

	userInviteSettingsSucceededSLO(attributes: { newState: string | null; productAri: string }): void {
		const { flow } = this.commonAttributes;

		this.fireTrack({
			// Matches naming from admin hub
			actionSubject: AnalyticsActionSubject.USER_INVITE_SETTINGS,
			action: AnalyticsAction.UPDATED,
			attributes: {
				flow,
				...attributes,
			},
		});
	}

	userInviteSettingsFailedSLO(
		attributes: {
			proposedNewState: string;
			productAri: string;
		} & FailedFetchAttributes,
	): void {
		const { flow } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.SLO_USER_INVITE_SETTINGS,
			action: AnalyticsAction.UPDATE_FAILED,
			attributes: {
				flow,
				...attributes,
			},
		});
	}

	featuredUserTagClicked(attr: { featuredUser: FeaturedUser }): void {
		const { source, userRole } = this.commonAttributes;
		const attributes = {
			featuredUserId: attr.featuredUser.id,
			featuredUserSource: attr.featuredUser.source,
		};
		this.fireUI({
			actionSubject: AnalyticsActionSubject.FEATURED_USER_TAG,
			action: AnalyticsAction.CLICKED,
			attributes: {
				integration: source,
				permissionLevel: userRole,
				...attributes,
			},
		});
	}

	featuredUsersInviteSucceeded(attributes: {
		featuredUsersInvited: number;
		totalUsersInvited: number;
	}): void {
		const { source, userRole } = this.commonAttributes;

		this.fireTrack({
			actionSubject: AnalyticsActionSubject.FEATURED_USERS,
			action: AnalyticsAction.INVITED,
			attributes: {
				integration: source,
				permissionLevel: userRole,
				...attributes,
			},
		});
	}

	copyLinkButtonClicked({ originAttributes }: { originAttributes: OriginAnalyticsAttributes }): void {
		this.fireUI({
			action: AnalyticsAction.CLICKED,
			actionSubject: AnalyticsActionSubject.BUTTON,
			actionSubjectId: 'copyInviteLink',
			attributes: {
				...this.commonAttributes,
				...originAttributes,
			},
		});
	}

	onSubmitFailed(attributes: { isInternal: boolean; isSuccessModal: boolean; error: string }):void {
		const { source, userRole } = this.commonAttributes;

		this.fireOperational({
			actionSubject: AnalyticsActionSubject.CORE_INVITE_COMPONENT,
			action: AnalyticsAction.FAILED,
			actionSubjectId: 'onSubmit',
			attributes: {
				...attributes,
				permissionLevel: userRole,
				integration: source,
			},
		});
	}
}
